/**
 * ShopSales Container
 * --
 * 작성자 - Owen
 * 작성일 - 2025 07 11 금요일
 * 마지막 수정일 - 2025 07 11 금요일
 */
import React from 'react';
import ShopSalesPresenter from './ShopSalesPresenter';
import Logger from 'utils/logger';
import { useParams } from 'react-router-dom';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';
import dayjs from 'dayjs';
import { useGetParcelServices } from 'hooks/ParcelServiceHooks';
import { useGetRentals } from 'hooks/RentalHooks';
import MessageAlert from 'utils';
import { downloadXlsx } from 'utils/XlsxDownloader';
import { useGetShopOrders } from 'hooks/ShopOrderHooks';

const logger = new Logger('ShopSalesContainer');

const ShopSalesContainer = (props) => {
  /**
   * SECTION - Route
   */
  const { type } = useParams();
  /**!SECTION - Route */

  /**
   * SECTION - Props
   */
  const { defaultShop = null, auth = 'ADMIN' } = props;
  /**!SECTION - Props */

  /**
   * SECTION - State
   */
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedShop, setSelectedShop] = React.useState(defaultShop);
  const [dateRange, setDateRange] = React.useState({
    startDate: 0,
    endDate: 0,
  });
  const [orderList, setOrderList] = React.useState({
    delivery: [],
    subscription: [],
    rental: [],
  });
  const [summaryColumns, setSummaryColumns] = React.useState([]);
  const [tableColumns, setTableColumns] = React.useState([]);

  const [disabledColumns, setDisabledColumns] = React.useState([]);
  /**!SECTION - State */

  /**
   * SECTION - Query Data
   */
  // 입점업체 목록
  const { data: Shops } = useGetShopInformations(
    {
      join_tables: '["shop_information_file"]',
    },
    {
      enabled: auth === 'ADMIN' ? true : !!selectedShop,
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        const filtered = data.filter((shop) => shop.type !== 'STORE');
        return filtered;
      },
    },
  );
  // ShopOrder 주문목록
  const { data: ShopOrder } = useGetShopOrders(
    {
      start_date: dayjs(dateRange.startDate).startOf('day').unix(),
      end_date: dayjs(dateRange.endDate).endOf('day').unix(),
      shop_information_id: selectedShop,
    },
    {
      enabled:
        selectedShop && dateRange.startDate && dateRange.endDate ? true : false,
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
    },
  );
  // 주문목록
  const { data: Parcel } = useGetParcelServices(
    {
      join_tables: '["refund"]',
      start_date: dayjs(dateRange.startDate).startOf('day').unix(),
      end_date: dayjs(dateRange.endDate).endOf('day').unix(),
      shop_information_id: selectedShop,
      get_type: 'PAYMENT',
    },
    {
      //   queryKey: [selectedShop, dateRange.startDate, dateRange.endDate],
      enabled:
        selectedShop && dateRange.startDate && dateRange.endDate ? true : false,
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
    },
  );
  // 렌탈목록
  const { data: Rental } = useGetRentals(
    {
      join_tables: '["refund"]',
      start_date: dayjs(dateRange.startDate).startOf('day').unix(),
      end_date: dayjs(dateRange.endDate).endOf('day').unix(),
      shop_information_id: selectedShop,
      get_type: 'PAYMENT',
    },
    {
      //   queryKey: [selectedShop, dateRange.startDate, dateRange.endDate],
      enabled:
        selectedShop && dateRange.startDate && dateRange.endDate ? true : false,
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
    },
  );
  /**!SECTION - Query Data */

  /**
   * SECTION - Function
   */
  /**
   * 검색 기간 선택
   * --
   */
  const handleSelectPeriod = (value) => {
    if (!selectedShop) {
      MessageAlert.info('업체를 선택해주세요!');
      return;
    }

    setDateRange(value);
  };

  /**
   * 주문 데이터 정제
   * --
   */
  const getOrderData = React.useCallback(() => {
    if (!selectedShop) return;

    setIsLoading(true);

    const newData = Parcel.filter((i) => i.payment_date).reduce(
      (acc, cur) => {
        const { type } = cur;

        if (type === 'SUBSCRIPTION') {
          acc['subscription'].push(cur);
        } else {
          acc['delivery'].push(cur);
        }

        return acc;
      },
      {
        delivery: [],
        subscription: [],
        rental: Rental,
      },
    );

    setTimeout(() => {
      setIsLoading(false);
      setOrderList(newData);
    }, 1000);
  }, [Rental, Parcel, selectedShop]);

  /**
   * 엑셀 다운로드
   * --
   */
  const handleXlsxDownload = React.useCallback(
    (tit = 'sales', d = []) => {
      try {
        setIsLoading(true);

        // React 엘리먼트로 작성된 제목을 원본 컬럼 변경 없이 엑셀용 문자열로 변환합니다.
        const getColumnTitle = (title) => {
          if (typeof title === 'string') return title;
          if (!React.isValidElement(title)) return '';

          return React.Children.toArray(title.props?.children)
            .map((child) => {
              if (typeof child === 'string') return child;
              if (!React.isValidElement(child)) return '';

              return React.Children.toArray(child.props?.children)
                .filter((value) => typeof value === 'string')
                .join('');
            })
            .join(' ')
            .trim();
        };

        downloadXlsx({
          columns: tableColumns?.map((column) => ({
            ...column,
            title: getColumnTitle(column?.title),
          })),
          rows: d,
          fileName: `${tit} 리포트`,
        });
      } catch (err) {
        logger.error('handleXlsxDownload', err);
        MessageAlert.error('엑셀 다운로드 실패!');
      } finally {
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      }
    },
    [tableColumns],
  );

  /**
   * 체크박스 핸들러
   * --
   * @param {Array} checked
   */
  const handleCheckbox = (checked) => {
    setDisabledColumns(checked);
  };
  /**!SECTION - Function */

  /**
   * SECTION - Hooks
   */
  /**
   * 주문 데이터 정제 호출
   * --
   */
  React.useEffect(() => {
    if (selectedShop && dateRange.startDate && dateRange.endDate) {
      getOrderData();
    }
  }, [dateRange, getOrderData, selectedShop]);

  React.useEffect(() => {
    if (summaryColumns.length) {
      const col = summaryColumns.map((item) => item.key);
      setDisabledColumns(col);
    }
  }, [summaryColumns]);
  /**!SECTION - Hooks */

  /**
   * SECTION - Props
   */
  const PROPS = {
    isLoading,
    auth,
    params: type,
    shopInformation: Shops,
    selectedShop,
    delivery: orderList.delivery,
    subscription: orderList.subscription,
    rental: orderList.rental,
    orders: ShopOrder.filter((i) => i.payment_completion_date),
    onSelect: (shopId) => {
      setSelectedShop(shopId);
    },
    onSelectPeriod: handleSelectPeriod,
    period: [dateRange.startDate, dateRange.endDate],

    summaryColumns,
    setSummaryColumns,
    setTableColumns,
    onDownload: handleXlsxDownload,

    disabledColumns,
    onCheckbox: handleCheckbox,
  };
  /**!SECTION - Props */

  /**
   * SECTION - Render
   */
  return (
    <React.Fragment>
      <ShopSalesPresenter {...PROPS} />
    </React.Fragment>
  );
  /**!SECTION - Render */
};

export default ShopSalesContainer;
